'use client';
import { useState } from 'react';
import Image from 'next/image';
import { alpha, Box, CircularProgress, SxProps, Theme, Typography, useTheme } from '@mui/material';

import { SquareIcon } from './SquareIcon';

interface AvatarProps {
  src?: string | null;
  alt: string;
  size?: number;
  isLoading?: boolean;
  sx?: SxProps<Theme>;
  fallbackLabel?: string;
  fallback?: React.ReactNode;
}

function Avatar({ src, alt, size = 44, isLoading, sx, fallbackLabel, fallback }: AvatarProps) {
  const theme = useTheme();
  const [hasError, setHasError] = useState(false);
  const isDark = theme.palette.mode === 'dark';
  const displayLabel = fallbackLabel || (alt || '?').charAt(0).toUpperCase();

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '16px',
        bgcolor: isDark ? alpha(theme.palette.text.primary, 0.05) : 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: isDark ? 'none' : 'inset 0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        position: 'relative',
        ...sx,
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 1,
          }}
        >
          <CircularProgress size={size / 2} color="inherit" sx={{ color: '#ffffff' }} />
        </Box>
      )}
      {src && !hasError ? (
        <Image
          src={src}
          alt={alt}
          fill
          sizes={`${size}px`}
          style={{ objectFit: 'cover' }}
          unoptimized
          key={src}
          onError={() => setHasError(true)}
        />
      ) : (
        fallback || <SquareIcon src={null} size={size} borderRadius="4px" />
      )}
    </Box>
  );
}

export default Avatar;
